import { useMemo, useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';

import { useProjectScopedEffect } from '../stores/project';
/**
 * L4.3 (round-5) — admin view for captured marketing leads.
 *
 * Reads /v1/marketing/leads (tenant-scoped). Filter by lead type +
 * date range; table only — CRUD on leads is intentionally read-only
 * in v1 because the lead lifecycle lives in Shakti's CRM, not here.
 */

type LeadType = 'newsletter' | 'demo' | 'whitepaper' | 'champion' | 'all';

interface LeadRow {
  id: string;
  email: string;
  lead_type: string;
  company: string | null;
  role: string | null;
  source_page: string | null;
  created_at: string;
}

export function MarketingAdminRoute(): JSX.Element {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<LeadType>('all');

  // N5.5 / W1.1 (v1.1) — project-scoped; aborts in-flight on switch.
  useProjectScopedEffect(
    (signal) => {
      setLoading(true);
      setError(null);
      shaktiApi
        .listMarketingLeads(filter === 'all' ? undefined : filter, { signal })
        .then((r) => {
          if (!signal.aborted) setLeads(r.leads);
        })
        .catch((e: unknown) => {
          if (signal.aborted) return;
          setError(
            e instanceof ApiError ? e.body?.error ?? e.message : 'Failed to load leads',
          );
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [filter],
  );

  const grouped = useMemo(() => {
    const tally: Record<string, number> = {};
    leads.forEach((l) => {
      tally[l.lead_type] = (tally[l.lead_type] ?? 0) + 1;
    });
    return tally;
  }, [leads]);

  return (
    <section aria-labelledby="ml-title" className="mx-auto flex max-w-5xl flex-col gap-5">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/marketing
        </span>
        <h1 id="ml-title" className="text-2xl font-semibold text-fg-primary">
          Marketing leads
        </h1>
        <p className="text-sm text-fg-secondary">
          Captured by the public <code>/v1/marketing/*</code> endpoints. Read-only in v1 — lead
          lifecycle lives in the CRM.
        </p>
      </header>

      {error ? (
        <div
          role="alert"
          className="rounded-sm border border-status-error/40 bg-status-error/10 px-3 py-2 text-xs text-status-error"
        >
          {error}
        </div>
      ) : null}

      <div className="shakti-card flex items-center justify-between p-4">
        <label className="flex items-center gap-2 text-xs">
          <span className="text-fg-muted">Filter</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as LeadType)}
            className="rounded-sm border border-border-subtle bg-bg-base px-2 py-1 text-xs"
          >
            <option value="all">All</option>
            <option value="newsletter">Newsletter</option>
            <option value="demo">Demo request</option>
            <option value="whitepaper">Whitepaper download</option>
            <option value="champion">Champion enrollment</option>
          </select>
        </label>
        <span className="text-xs text-fg-muted">
          {loading ? 'Loading…' : `${leads.length} leads`}
        </span>
      </div>

      <section className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {(['newsletter', 'demo', 'whitepaper', 'champion'] as const).map((t) => (
          <div
            key={t}
            className="shakti-card flex flex-col gap-1 p-3"
            aria-label={`${t} tally`}
          >
            <div className="text-2xs uppercase tracking-wider text-fg-muted">{t}</div>
            <div className="text-2xl font-semibold">{grouped[t] ?? 0}</div>
          </div>
        ))}
      </section>

      <section className="shakti-card p-0">
        <table className="w-full text-xs">
          <thead className="bg-bg-raised">
            <tr className="text-left text-2xs uppercase tracking-wider text-fg-muted">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Captured</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-fg-muted">
                  No leads yet.
                </td>
              </tr>
            ) : (
              leads.map((l) => (
                <tr key={l.id} className="border-t border-border-subtle">
                  <td className="px-4 py-2 shakti-mono">{l.email}</td>
                  <td className="px-4 py-2 uppercase text-accent">{l.lead_type}</td>
                  <td className="px-4 py-2">{l.company ?? '—'}</td>
                  <td className="px-4 py-2">{l.role ?? '—'}</td>
                  <td className="px-4 py-2 text-fg-muted">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </section>
  );
}
